import { forwardRef } from 'react';
import { Formik } from 'formik';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InputPriceRate from 'components/others/InputPriceRate';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';

export interface ChangePriceSaleValues {
    price: number | string;
    priceSale: number | string;
}

interface ChangePriceSaleFormProps {
    price: number | string;
    priceSale: number | string;
    rate: number;
    hidePurchase: boolean;
    readOnly?: boolean;
    /** modal — кнопки діалогу; tab — власний футер у офканвасі */
    variant: 'modal' | 'tab';
    onSubmit: (values: ChangePriceSaleValues) => void | Promise<void>;
}

const ChangePriceSaleForm = forwardRef<HTMLFormElement, ChangePriceSaleFormProps>(
    function ChangePriceSaleForm(
        { price, priceSale, rate, hidePurchase, readOnly = false, variant, onSubmit },
        ref,
    ) {
        const { t } = useTranslation(['Objects', 'Warehouses', 'common']);
        const isTab = variant === 'tab';
        const priceId = isTab ? 'tabInputPrice' : 'inputPrice';
        const priceSaleId = isTab ? 'tabInputPriceSale' : 'inputPriceSale';

        return (
            <Formik
                enableReinitialize={isTab}
                initialValues={{ price, priceSale }}
                onSubmit={(values) => onSubmit(values)}
            >
                {({ handleSubmit, setFieldValue, values, isSubmitting }) => {
                    const locked = readOnly || isSubmitting;
                    const fields = (
                        <>
                            {isTab ? (
                                <p className="text-muted mb-3">{t('productPopup.editPriceHint')}</p>
                            ) : null}
                            {hidePurchase ? null : (
                                <div className="mb-3">
                                    <label className="form-label text-muted w-100 d-flex justify-content-between">
                                        {t('Warehouses:labelPrice')}
                                    </label>
                                    <InputPriceRate
                                        id={priceId}
                                        rate={rate}
                                        allowNegative={false}
                                        disabled={locked}
                                        value={values.price as number}
                                        required={!readOnly}
                                        onValueChange={(val) => {
                                            setFieldValue('price', val.value);
                                        }}
                                    />
                                </div>
                            )}
                            <div>
                                <label className="form-label text-muted">{t('Warehouses:labelPriceSale')}</label>
                                <InputPriceRate
                                    id={priceSaleId}
                                    rate={rate}
                                    allowNegative={false}
                                    disabled={locked}
                                    value={values.priceSale as number}
                                    required={!readOnly}
                                    onValueChange={(val) => {
                                        setFieldValue('priceSale', val.value);
                                    }}
                                />
                            </div>
                        </>
                    );

                    if (!isTab) {
                        return (
                            <form ref={ref} onSubmit={handleSubmit} style={{ marginTop: '-20px' }}>
                                {fields}
                                <input type="submit" className="d-none" />
                            </form>
                        );
                    }

                    return (
                        <form ref={ref} className="d-flex flex-column h-100" onSubmit={handleSubmit}>
                            <div className="off-create-team-form d-flex flex-column h-100">
                                <div className="off-create-team-content px-3 pt-3">{fields}</div>
                                {!readOnly && (
                                    <div className="p-3 offcanvas-footer off-create-team-footer">
                                        <Button
                                            type="submit"
                                            variant="info"
                                            className="min100px"
                                            disabled={isSubmitting}
                                        >
                                            {spinnerTitle(
                                                <>
                                                    <SvgSave className="me-2 mtop-2" />
                                                    {t('common:buttons.save')}
                                                </>,
                                                isSubmitting,
                                                t('common:buttons.saving'),
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        );
    },
);

export default ChangePriceSaleForm;
